import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getNotesApi, addNoteApi, updateNoteApi, deleteNoteApi } from '../../apis/apis'
import { Paginated, Note, NoteSearchParams } from '../../apis/types'
import type { RootState, AppDispatch } from '../../shared/redux/store'

const SLICE_NAME = 'NOTES_SLICE'

function notesQueryKey(params: NoteSearchParams): string {
  const tags = params.tags?.slice().sort().join(',') ?? ''
  const startDate =
    params.startDate instanceof Date ? params.startDate.toISOString() : (params.startDate ?? '')
  const endDate = params.endDate instanceof Date ? params.endDate.toISOString() : (params.endDate ?? '')
  return [
    params.sourceEntity ?? '',
    params.body ?? '',
    tags,
    startDate,
    endDate,
    params.sortIsPinned ?? '',
    params.page ?? 0,
    params.sortCreatedAt ?? '',
    params.resultsPerPage ?? 0,
  ].join('|')
}

export const getNotes = createAsyncThunk<
  Paginated<Note>,
  NoteSearchParams,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getNotes`, async (query: NoteSearchParams, { rejectWithValue }) => {
  try {
    const response = await getNotesApi(query)
    return { ...response }
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

export const addNote = createAsyncThunk<
  Note,
  { sourceEntity: string; body: string },
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/addNote`, async ({ sourceEntity, body }: { sourceEntity: string; body: string }, { rejectWithValue }) => {
  try {
    const response = await addNoteApi({ sourceEntity, body })
    return { ...response, sourceEntity }
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

export const updateNote = createAsyncThunk<
  Note,
  { noteCuid: string; body?: string; isPinned?: boolean; tags?: string[] },
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(
  `${SLICE_NAME}/updateNote`,
  async ({ noteCuid, body, isPinned, tags }: { noteCuid: string; body?: string; isPinned?: boolean; tags?: string[] }, { rejectWithValue }) => {
    try {
      const response = await updateNoteApi({ noteCuid, body, isPinned, tags })
      return { ...response }
    } catch (err) {
      return rejectWithValue({
        err: err !== undefined,
      })
    }
  }
)

export const deleteNote = createAsyncThunk<
  Note,
  { noteCuid: string },
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/deleteNote`, async ({ noteCuid }: { noteCuid: string }, { rejectWithValue }) => {
  try {
    const response = await deleteNoteApi({ noteCuid })
    return { ...response }
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

interface state {
  loading: boolean
  error: boolean
  noteRequestPhase: { kind: 'idle' } | { kind: 'in-progress' } | { kind: 'failed' }
  notesQueryKey: string | null
  notes: Note[]
  total: number
  resultsPerPage: number
  page: number
}

const initialState: state = {
  loading: false,
  error: false,
  noteRequestPhase: { kind: 'idle' },
  notesQueryKey: null,
  notes: [],
  total: 0,
  resultsPerPage: 0,
  page: 0,
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getNotes.pending, (state, action) => {
      const newKey = notesQueryKey(action.meta.arg)
      const prevEntity = state.notesQueryKey?.split('|')[0]
      const newEntity = action.meta.arg.sourceEntity ?? ''
      if (prevEntity !== newEntity) {
        state.notes = []
        state.total = 0
      }
      state.noteRequestPhase = { kind: 'in-progress' }
      state.notesQueryKey = newKey
    })
    builder.addCase(getNotes.fulfilled, (state, action) => {
      if (notesQueryKey(action.meta.arg) !== state.notesQueryKey) return
      const { payload } = action
      state.noteRequestPhase = { kind: 'idle' }
      state.notes = payload.results || []
      state.total = payload.total ?? 0
      state.resultsPerPage = payload.resultsPerPage ?? 0
      state.page = payload.page ?? 0
    })
    builder.addCase(getNotes.rejected, (state, { payload, error, meta }) => {
      if (notesQueryKey(meta.arg) !== state.notesQueryKey) return
      if (payload != null) {
        state.noteRequestPhase = { kind: 'failed' }
      } else {
        state.error = error !== undefined
      }
    })

    builder.addCase(addNote.pending, (state) => {
      state.noteRequestPhase = { kind: 'in-progress' }
    })
    builder.addCase(addNote.fulfilled, (state, { payload }) => {
      state.noteRequestPhase = { kind: 'idle' }
      state.notes.unshift(payload)
    })
    builder.addCase(addNote.rejected, (state, { payload, error }) => {
      if (payload != null) {
        state.noteRequestPhase = { kind: 'failed' }
      } else {
        state.error = error !== undefined
      }
    })

    builder.addCase(updateNote.pending, (state) => {
      state.noteRequestPhase = { kind: 'in-progress' }
    })
    builder.addCase(updateNote.fulfilled, (state, { payload }) => {
      state.noteRequestPhase = { kind: 'idle' }
      state.notes = state.notes.map((note) => (note.cuid === payload.cuid ? { ...payload } : note))
    })
    builder.addCase(updateNote.rejected, (state, { payload, error }) => {
      if (payload != null) {
        state.noteRequestPhase = { kind: 'failed' }
      } else {
        state.error = error !== undefined
      }
    })

    builder.addCase(deleteNote.pending, (state) => {
      state.noteRequestPhase = { kind: 'in-progress' }
    })
    builder.addCase(deleteNote.fulfilled, (state, { payload }) => {
      state.noteRequestPhase = { kind: 'idle' }
      if (payload != null) {
        state.notes = state.notes.filter((note) => note.cuid !== payload.cuid)
      }
    })
    builder.addCase(deleteNote.rejected, (state, { payload, error }) => {
      if (payload != null) {
        state.noteRequestPhase = { kind: 'failed' }
      } else {
        state.error = error !== undefined
      }
    })
  },
})

export const notesReducer = slice.reducer
